/**
 * Shared contract for the Pipedream Connect URL flow.
 *
 *   POST /api/integrations/pipedream/connect
 *
 * Authed (Bearer JWT). Returns a short-lived URL the user clicks to
 * authorize a third-party service via Pipedream's hosted OAuth. After
 * OAuth completes, Pipedream's webhook hits Ano (separate inbound
 * route) and Ano persists the connection. Pollers then look the row up
 * via `GET /api/connections?workspace_id=…` matching the
 * `expected_connection_name` returned here.
 *
 * Sibling inbound webhook contract is intentionally not exported —
 * that's a server-internal concern (HMAC-verified, no client caller).
 */

import { z } from "zod";

import {
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const requestPipedreamConnectionBodySchema = z.object({
  /**
   * Pipedream app slug. Pipedream catalog at https://pipedream.com/apps.
   * Common: "linear", "github", "gmail", "notion", "hubspot", "posthog".
   */
  app: z.string().min(1),
  /**
   * Workspace the connection lands in. Omit to use the user's first
   * workspace (matches MCP convention for `list_*` tools).
   */
  workspace_id: z.string().min(1).optional(),
});
export type RequestPipedreamConnectionBody = z.infer<
  typeof requestPipedreamConnectionBodySchema
>;

export const requestPipedreamConnectionResponseSchema = z.object({
  auth_url: z.string().url(),
  expires_at: z.string(),
  workspace_id: z.string(),
  /**
   * Deterministic name the persisted row will carry once OAuth completes
   * (`pipedream:<app>:<userId>`). Use this to poll
   * `GET /api/connections?workspace_id=…` for the exact row.
   */
  expected_connection_name: z.string(),
});
export type RequestPipedreamConnectionResponse = z.infer<
  typeof requestPipedreamConnectionResponseSchema
>;

export const requestPipedreamConnectionPath = () =>
  "/api/integrations/pipedream/connect";

export async function requestPipedreamConnectionRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: RequestPipedreamConnectionBody;
  fetchImpl?: FetchLike;
}): Promise<RequestPipedreamConnectionResponse> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = requestPipedreamConnectionBodySchema.parse(body);
  const res = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${requestPipedreamConnectionPath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );
  if (!res.ok) {
    throw await parseErrorResponse(res, "Failed to request connection");
  }
  return requestPipedreamConnectionResponseSchema.parse(await res.json());
}

/**
 * Backfill / reconcile contract.
 *
 *   POST /api/integrations/pipedream/backfill
 *
 * Authed (Bearer JWT). Reconciles the caller's Pipedream-vault accounts
 * into `automation_connections` by querying Pipedream's `accounts.list`.
 * This is the reliable, signing-agnostic RECORD path — it does not depend
 * on Pipedream's connection webhook firing. Safe to call repeatedly: the
 * server persist is `ON CONFLICT (workspace_id, name) DO UPDATE`.
 *
 * Both body fields are optional — omit `app` to reconcile every connected
 * app, omit `workspace_id` to use the caller's first workspace.
 */

export const backfillPipedreamConnectionsBodySchema = z.object({
  /** App slug filter (e.g. "linear"). Omit to backfill all apps. */
  app: z.string().min(1).optional(),
  /** Workspace to reconcile into. Omit to use the caller's first. */
  workspace_id: z.string().min(1).optional(),
});
export type BackfillPipedreamConnectionsBody = z.infer<
  typeof backfillPipedreamConnectionsBodySchema
>;

export const backfillPipedreamConnectionsResponseSchema = z.object({
  workspace_id: z.string(),
  /** Connections persisted or refreshed during this call. */
  persisted: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      external_account_id: z.string().nullable(),
      kind: z.string(),
      status: z.string(),
    }),
  ),
  /**
   * Vault accounts the server saw but didn't persist, each with a reason
   * (e.g. missing app slug). `accountId` may be absent when the account
   * itself had no id.
   */
  skipped: z.array(
    z.object({
      accountId: z.string().optional(),
      reason: z.string(),
    }),
  ),
});
export type BackfillPipedreamConnectionsResponse = z.infer<
  typeof backfillPipedreamConnectionsResponseSchema
>;

export const backfillPipedreamConnectionsPath = () =>
  "/api/integrations/pipedream/backfill";

export async function backfillPipedreamConnectionsRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body?: BackfillPipedreamConnectionsBody;
  fetchImpl?: FetchLike;
}): Promise<BackfillPipedreamConnectionsResponse> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = backfillPipedreamConnectionsBodySchema.parse(body ?? {});
  const res = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${backfillPipedreamConnectionsPath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );
  if (!res.ok) {
    throw await parseErrorResponse(res, "Failed to backfill connections");
  }
  return backfillPipedreamConnectionsResponseSchema.parse(await res.json());
}
